<script>
import { projectState } from "@app/store/projectStore.svelte.js";
import { uiState, toggleKnpLookup, toggleAssistant } from "@app/store/uiStore.svelte.js";
import { isToolbarButtonAllowed } from "@app/store/permissionStore.svelte.js";
import { showModal } from "@app/store/modalStore.svelte.js";
import { externalDataState } from "@app/store/externalDataStore.svelte.js";
import { insertHistoryAction } from "@app/store/historyStore.svelte.js";
import { speakerState } from "@app/store/speakerStore.svelte.js";
import { eventGroupState, isSelectedEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { environment } from "@app/store/envStore.svelte.js";
import { authState } from "@app/store/authStore.svelte.js";
import FileMenu from "./menus/FileMenu.svelte";
import EditMenu from "./menus/EditMenu.svelte";
import FormatMenu from "./menus/FormatMenu.svelte";
import TimecodeMenu from "./menus/TimecodeMenu.svelte";
import InsertMenu from "./menus/InsertMenu.svelte";
import AiToolsMenu from "./menus/AiToolsMenu.svelte";
import WorkspacesMenu from "./menus/WorkspacesMenu.svelte";
import TeamMenu from "./menus/TeamMenu.svelte";
import ThemeMenu from "./menus/ThemeMenu.svelte";
import LanguageMenu from "./menus/LanguageMenu.svelte";
import HelpMenu from "./menus/HelpMenu.svelte";
import CliMenu from "./menus/CliMenu.svelte";
import MenuBar from "./menu/MenuBar.svelte";
import * as m from "@app/paraglide/messages.js";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import {
    callParentCancel,
    callParentExportSubtitle,
} from "@app/lib/audio/parentTts.js";
/* CC LIB */
import _Speaker from "@cclib/classes/speaker.js";
import _Event from "@cclib/classes/event.js";
import _EventGroup from "@cclib/classes/eventGroup.js";
import _Project from "@cclib/classes/project.js";
import decode from "@cclib/functions/decode.js";
import defaults from "@cclib/lib/defaults.js";
import alignCaptionFile from "@cclib/functions/special/alignCaptionFile.js";
import convertToHtml from "@cclib/functions/quill/convertToHtml.js";

let loading = $state(false);
let isLocked = $derived(isSelectedEventGroupLocked());
let isFullscreen = $state(false);

async function triggerParentCancel() {
    try {
        await callParentCancel();
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
    }
}

async function triggerParentExportSubtitle() {
    try {
        await callParentExportSubtitle();
    } catch (err) {
        toast.push(err.message, { classes: ["toast-danger"] });
    }
}

function updateSpeakerStore(evg) {
    evg.events.forEach((event, index, events) => {
        if (event.speakers && event.speakers.length > 0) {
            let speakerIndex = speakerState.findIndex((sp) => {
                return sp.name == "Speaker" + event.speakers[0];
            });

            if (speakerIndex === -1) {
                let newSpeaker = {
                    ...new _Speaker({
                        name: "Speaker" + event.speakers[0],
                    }),
                };
                speakerState.push(newSpeaker);
                events[index].speakers = [newSpeaker];
            } else {
                events[index].speakers = [speakerState[speakerIndex]];
            }
        }
    });
}

async function automaticSync() {
    try {
        if (loading) {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        //Use Sweet alert to confirm that the user wants to proceed with the automatic sync process.
        let swalRes = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.automatic_sync(),
            text: m.automatic_sync_confirm(),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.yes(),
            cancelButtonText: m.no(),
        });

        if (!swalRes.isConfirmed) {
            return;
        }

        toast.push(m.calculating_sync(), {
            classes: ["toast-info"],
        });

        loading = true;
        let eventGroupDefaults =
            JSON.parse(localStorage.getItem("cc-event-group-defaults")) || {};

        let transcripts = externalDataState.transcripts.filter(
            (transcript, tIndex, allTranscripts) => {
                if (transcript.language) {
                    return (
                        transcript.language.toLowerCase() ===
                        eventGroupState[
                            projectState.selected
                        ].language.toLowerCase()
                    );
                } else if (allTranscripts.length === 1) {
                    return true;
                } else {
                    return false;
                }
            },
        );

        if (transcripts.length === 0) {
            //Confirm with the user using Sweetalert that no transcript was found for the selected language.
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.no_transcript_found(),
                text: m.no_transcript_for_language({ language: eventGroupState[projectState.selected].language }),
                icon: "warning",
                confirmButtonText: m.ok(),
            });

            toast.push(
                m.no_transcript_for_language({ language: eventGroupState[projectState.selected].language }),
                {
                    classes: ["toast-danger"],
                },
            );

            return;
        }

        //Fetch the transcript json from the first transcript object using the url property.
        let transcript = transcripts[0];
        let res = await fetch(transcript.url);
        let resJson = await res.json();
        let srcProfile = transcript.profile;
        let options = new defaults.options({
            profile: srcProfile,
            formatOptions: [
                {
                    name: "Import Type",
                    type: "list",
                    values: ["subtitle", "transcription"],
                    selected: "word map",
                },
                {
                    name: "Max Characters",
                    type: "number-input",
                    values: "",
                    selected: 32,
                },
                {
                    name: "Max Lines",
                    type: "number-input",
                    values: "",
                    selected: 3,
                },
            ],
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
        });

        console.log(options);
        let wordMap = await decode(JSON.stringify(resJson), options);
        let eventGroup = JSON.parse(
            JSON.stringify(eventGroupState[projectState.selected]),
        );

        if (eventGroup.events.length === 0) {
            return;
        }

        //Calculate missing timecodes so that they don't get filtered out by the alignCaptionFile function.
        eventGroup.events.forEach((event, i, events) => {
            events[i].start = i + 0.25;
            events[i].end = i + 0.75;
        });

        let alignmentRes = await alignCaptionFile(
            eventGroup,
            wordMap,
            projectState.frameRate,
            false,
        );

        eventGroupState[projectState.selected].events =
            alignmentRes.eventGroup.events;

        if (alignmentRes.missingSegments.length > 0) {
            //Alert user of missing dialogue segments, and ask if they would like a new event group created with the missing dialogue.
            let swalRes = await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.confirmCancel,
                title: m.missing_dialogue(),
                text: m.missing_dialogue_confirm(),
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: m.yes(),
                cancelButtonText: m.no(),
            });

            //If user selects yes, create a new event group with the missing dialogue segments and add it to the event group state.
            if (swalRes.isConfirmed) {
                let newEventGroup = {
                    ...new _EventGroup({
                        name: m.event_group_missing_dialogue_name({ index: eventGroupState.length }),
                        type: "subtitle",
                        maxCps: eventGroupDefaults.maxCps || 9999,
                        maxWpm: eventGroupDefaults.maxWpm || 9999,
                        maxChars: eventGroupDefaults.maxChars || 9999,
                        maxLines: eventGroupDefaults.maxLines || 9999,
                        minDuration: eventGroupDefaults.minDuration || 0.5,
                        maxDuration: eventGroupDefaults.maxDuration || 12,
                        illegalChars: eventGroupDefaults.illegalChars,
                        fontFamily: eventGroupDefaults.fontFamily ?? "plexMono",
                        fontSize: eventGroupDefaults.fontSize || 16,
                    }),
                };

                alignmentRes.missingSegments.forEach((segment) => {
                    newEventGroup.events.push({
                        ...new _Event({
                            start: segment.start,
                            end: segment.end,
                            text: convertToHtml(segment.text),
                        }),
                    });
                });

                eventGroupState.push(newEventGroup);
            }
        }

        insertHistoryAction({
            name: "automatic sync", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
            ],
        });

        toast.push(m.timing_applied_success(), {
            classes: ["toast-success"],
        });
    } catch (err) {
        console.log(err, err.message);
    } finally {
        loading = false;
    }
}

async function automaticSpeakerId() {
    try {
        if (loading) {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        //Use Sweet alert to confirm that the user wants to proceed with the automatic sync process.
        let swalRes = await Swal.fire({
            ...SWAL_BASE,
            customClass: SWAL_CLASSES.confirmCancel,
            title: m.automatic_speaker_id_title(),
            text: m.automatic_speaker_id_confirm(),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: m.yes(),
            cancelButtonText: m.no(),
        });

        if (!swalRes.isConfirmed) {
            return;
        }

        toast.push(m.calculating_speakers(), {
            classes: ["toast-info"],
        });

        loading = true;

        let transcripts = externalDataState.transcripts.filter(
            (transcript, tIndex, allTranscripts) => {
                if (transcript.language) {
                    return (
                        transcript.language.toLowerCase() ===
                        eventGroupState[
                            projectState.selected
                        ].language.toLowerCase()
                    );
                } else if (allTranscripts.length === 1) {
                    return true;
                } else {
                    return false;
                }
            },
        );

        if (transcripts.length === 0) {
            //Confirm with the user using Sweetalert that no transcript was found for the selected language.
            await Swal.fire({
                ...SWAL_BASE,
                customClass: SWAL_CLASSES.ok,
                title: m.no_transcript_found(),
                text: m.no_transcript_for_language({
                    language: eventGroupState[projectState.selected].language,
                }),
                icon: "warning",
                confirmButtonText: m.ok(),
            });

            toast.push(
                m.no_transcript_for_language({
                    language: eventGroupState[projectState.selected].language,
                }),
                {
                    classes: ["toast-danger"],
                },
            );

            return;
        }

        //Fetch the transcript json from the first transcript object using the url property.
        let transcript = transcripts[0];
        let res = await fetch(transcript.url);
        let resJson = await res.json();
        let srcProfile = transcript.profile;
        let options = new defaults.options({
            profile: srcProfile,
            formatOptions: [
                {
                    name: "Import Type",
                    type: "list",
                    values: ["subtitle", "transcription"],
                    selected: "word map",
                },
                {
                    name: "Max Characters",
                    type: "number-input",
                    values: "",
                    selected: 32,
                },
                {
                    name: "Max Lines",
                    type: "number-input",
                    values: "",
                    selected: 3,
                },
            ],
            frameRate: projectState.frameRate,
            dropFrame: projectState.dropFrame,
        });

        console.log(options);
        let wordMap = await decode(JSON.stringify(resJson), options);
        let eventGroup = JSON.parse(
            JSON.stringify(eventGroupState[projectState.selected]),
        );

        if (eventGroup.events.length === 0) {
            return;
        }

        //Calculate missing timecodes so that they don't get filtered out by the alignCaptionFile function.
        eventGroup.events.forEach((event, i, events) => {
            events[i].start = i + 0.25;
            events[i].end = i + 0.75;
        });

        let alignmentRes = await alignCaptionFile(
            eventGroup,
            wordMap,
            projectState.frameRate,
            false,
        );

        eventGroupState[projectState.selected].events.forEach(
            (event, i, events) => {
                events[i].speakers = [];
            },
        );

        //Assign speakers to the events in the selected event group.
        eventGroupState[projectState.selected].events.forEach(
            (event, index, events) => {
                let eventMatch = alignmentRes.eventGroup.events.find((e) => {
                    return e.id === event.id;
                });

                if (
                    eventMatch &&
                    eventMatch.matches &&
                    eventMatch.matches.length > 0
                ) {
                    let speakers = eventMatch.matches.map((m) => {
                        return m.speakers[0];
                    });

                    let speakerCount = speakers.reduce((acc, speaker) => {
                        acc[speaker] = (acc[speaker] || 0) + 1;
                        return acc;
                    }, {});

                    let maxSpeaker = Object.keys(speakerCount).reduce((a, b) =>
                        speakerCount[a] > speakerCount[b] ? a : b,
                    );

                    events[index].speakers = [maxSpeaker];
                }
            },
        );

        //Call the update speaker store function to update the speaker store with the new speakers.
        updateSpeakerStore(eventGroupState[projectState.selected]);
        eventGroupState[projectState.selected] =
            eventGroupState[projectState.selected];

        insertHistoryAction({
            name: "auto assign speakers", //action name
            eventGroup: projectState.selected,
            snapshots: [
                {
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState),
                },
                {
                    store: "speakerState",
                    value: JSON.stringify(speakerState),
                },
            ],
        });

        toast.push(m.auto_assign_speakers_complete(), {
            classes: ["toast-success"],
        });
    } catch (err) {
        console.log(err, err.message);
    } finally {
        loading = false;
    }
}

function triggerAtelierePublishEvent() {
    const event = new CustomEvent("handleAtelierePublishEvent", {
        detail: {},
    });

    window.dispatchEvent(event);
    showModal("ateliereConnectRemote");
}

function toggleFullscreen() {
    if (!document.fullscreenElement) {
        // Enter fullscreen
        const elem = document.documentElement;
        if (elem.requestFullscreen) {
            elem.requestFullscreen();
        } else if (elem.webkitRequestFullscreen) { // Safari
            elem.webkitRequestFullscreen();
        } else if (elem.msRequestFullscreen) { // IE11
            elem.msRequestFullscreen();
        }
    } else {
        // Exit fullscreen
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { // Safari
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { // IE11
            document.msExitFullscreen();
        }
    }
}

// Track fullscreen state changes
$effect(() => {
    function handleFullscreenChange() {
        isFullscreen = !!document.fullscreenElement;
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange); // Safari
    document.addEventListener('msfullscreenchange', handleFullscreenChange); // IE11

    return () => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange);
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
});
</script>

<nav
    aria-label={m.primary_toolbar()}
    class="navbar navbar-expand bg-light shadow cc-toolbar-primary"
    style="height: 3vh;">
    <div class="container-fluid">
        {#if environment.iframe}
            <a class="navbar-brand" href="#!/">
                <img
                    src="./assets/img/logos/{environment.system}.png"
                    id="systemLogoImg"
                    alt={m.system_logo_alt()}
                    height="14"
                    class="align-baseline" />
            </a>
        {/if}
        <div class="collapse navbar-collapse" id="primary-toolbar">
            <MenuBar>
            <ul class="nav navbar-nav" aria-label={m.main_menu()} role="menubar">
                {#if !environment.iframe}
                    <FileMenu></FileMenu>
                {/if}
                <EditMenu></EditMenu>
                <FormatMenu></FormatMenu>
                <TimecodeMenu></TimecodeMenu>
                <InsertMenu></InsertMenu>
                {#if !environment.iframe || environment.adLicense}
                    <AiToolsMenu></AiToolsMenu>
                {/if}
                {#if environment.iframe}
                    <WorkspacesMenu></WorkspacesMenu>
                {/if}
                {#if environment.electron}
                    <CliMenu></CliMenu>
                {/if}
                {#if !environment.iframe}
                    <HelpMenu></HelpMenu>
                {/if}
            </ul>
            <ul
                class="nav navbar-nav ms-auto"
                aria-label={m.view_options_and_tools()}
                role="menubar">
                <LanguageMenu></LanguageMenu>
                <ThemeMenu></ThemeMenu>
                {#if isToolbarButtonAllowed("timeline")}
                <li class="nav-item" title={m.show_hide_timeline()}>
                    <a
                        class="nav-link {uiState.timeline
                            ? 'text-primary'
                            : 'text-muted'}"
                        href="#!/"
                        id="toggle-timeline"
                        aria-label={m.show_or_hide_timeline()}
                        aria-pressed={uiState.timeline}
                        role="button"
                        onclick={() => (uiState.timeline = !uiState.timeline)}
                        ><i class="bi bi-input-cursor" aria-hidden="true"></i
                        ></a>
                </li>
                {/if}
                <!-- List Preview -->
                {#if isToolbarButtonAllowed("listPreview")}
                <li class="nav-item" title={m.toggle_prev_next()}>
                    <a
                        class="nav-link {uiState.listPreview
                            ? 'text-primary'
                            : 'text-muted'}"
                        href="#!/"
                        id="toggle-preview"
                        aria-label={m.toggle_prev_next_desc()}
                        aria-pressed={uiState.listPreview}
                        role="button"
                        onclick={() =>
                            (uiState.listPreview = !uiState.listPreview)}
                        ><i class="bi bi-view-list" aria-hidden="true"></i>
                    </a>
                </li>
                {/if}
                <!-- Fullscreen Toggle -->
                {#if isToolbarButtonAllowed("fullscreen")}
                <li class="nav-item" title={m.toggle_fullscreen()}>
                    <a
                        class="nav-link {isFullscreen
                            ? 'text-primary'
                            : 'text-muted'}"
                        href="#!/"
                        id="toggle-fullscreen"
                        aria-label={m.toggle_fullscreen_mode()}
                        aria-pressed={isFullscreen}
                        role="button"
                        onclick={() => toggleFullscreen()}
                        ><i class="bi {isFullscreen ? 'bi-fullscreen-exit' : 'bi-fullscreen'}" aria-hidden="true"></i>
                    </a>
                </li>
                {/if}
                <!-- KNP Terminology Lookup -->
                {#if environment.online && authState.team && ["editor", "admin"].includes(authState?.user?.role)}
                    <li class="nav-item" title={m.knp_terminology_lookup()}>
                        <a
                            class="nav-link text-muted"
                            href="#!/"
                            id="toggle-knp-lookup"
                            aria-label={m.open_knp_lookup()}
                            role="button"                            
                            onclick={() => toggleKnpLookup()}
                            ><i class="bi bi-journal-text" aria-hidden="true"></i>
                        </a>
                    </li>
                {/if}

                {#if projectState.type === "team"}
                    <div class="vr align-self-center"></div>
                    <TeamMenu></TeamMenu>
                {/if}
                {#if environment.system === "ateliere" && environment.iframe}
                    <div class="vr align-self-center"></div>
                    <li class="nav-item" title={m.publish_to_connect()}>
                        <a
                            class="nav-link"
                            href="#!/"                            
                            onclick={() => triggerAtelierePublishEvent()}
                            ><img
                                width="15px"
                                src="./assets/img/logos/ateliere-icon.png"
                                alt={m.ateliere_logo_alt()} /> {m.publish()}</a>
                    </li>
                {/if}
                {#if externalDataState.platform === "Iconik"}
                    <div class="vr align-self-center"></div>
                    <li class="nav-item" title={m.reload_proxy()}>
                        <a
                            aria-label={m.reload_proxy()}
                            class="nav-link"
                            href="#!/"
                            onclick={() => showModal("iconikGetProxy")}
                            ><i class="bi bi-file-earmark-play"></i><sub
                                ><i
                                    class="bi bi-arrow-clockwise text-custom-xsmall"
                                ></i
                                ></sub
                            ></a>
                    </li>
                    <li class="nav-item" title={m.iconik_toolbar_metadata_title()}>
                        <a
                            aria-label={m.iconik_toolbar_metadata_title()}
                            class="nav-link"
                            href="#!/"
                            onclick={() => showModal("iconikMetadata")}
                            ><i class="bi bi-card-text"></i></a>
                    </li>
                    <li class="nav-item" title={m.publish_to_iconik()}>
                        <a
                            class="nav-link fw-bold"
                            style="color: #00E1BE !important;"
                            href="#!/"
                            onclick={() => showModal("iconikPublish")}
                            ><img
                                width="15px"
                                src="./assets/img/logos/iconik-icon.ico"
                                alt={m.iconik_logo_alt()} /> {m.publish()}</a>
                    </li>
                {:else if externalDataState.platform === "Work Order"}
                    <div class="vr align-self-center"></div>
                    {#if externalDataState.transcripts?.length > 0}
                        <li class="nav-item" title={m.automatic_sync()}>
                            <a
                                class="nav-link {loading || isLocked ? 'disabled' : ''}"
                                href="#!/"
                                onclick={() => automaticSync()}>
                                {#if loading}
                                    <div
                                        class="spinner-border spinner-border-sm"
                                        role="status">
                                        <span class="visually-hidden"
                                            >{m.loading()}</span>
                                    </div>
                                {:else}
                                    <i class="bi bi-bar-chart-steps"></i>
                                {/if}
                            </a>
                        </li>
                        <li
                            class="nav-item"
                            title={m.automatic_speaker_id()}>
                            <a
                                class="nav-link {loading || isLocked ? 'disabled' : ''}"
                                href="#!/"
                                onclick={() => automaticSpeakerId()}>
                                {#if loading}
                                    <div
                                        class="spinner-border spinner-border-sm"
                                        role="status">
                                        <span class="visually-hidden"
                                            >{m.loading()}</span>
                                    </div>
                                {:else}
                                    <i class="bi bi-person-check-fill"></i>
                                {/if}
                            </a>
                        </li>
                        <div class="vr align-self-center"></div>
                    {/if}
                    <li class="nav-item" title={m.publish_work_order()}>
                        <a
                            class="nav-link fw-bold"
                            href="#!/"
                            onclick={() => showModal("workOrderPublish")}
                            ><i class="bi bi-check-lg"></i> {m.publish()}</a>
                    </li>
                {:else if environment.online}
                    <div class="vr align-self-center"></div>
                    <li class="nav-item" title={m.ai_assistant()}>
                        <a
                            aria-label={m.ai_assistant()}
                            aria-pressed={uiState.assistant.open}
                            class="nav-link {uiState.assistant.open ? 'text-primary' : ''}"
                            href="#!/"
                            id="toggle-ai-assistant"
                            role="button"
                            onclick={() => toggleAssistant()}
                            ><i class="bi bi-stars" aria-hidden="true"></i></a>
                    </li>
                {/if}
                {#if environment.parentCancel || environment.parentExportSubtitle}
                    <div class="vr align-self-center"></div>
                    {#if environment.parentCancel}
                        <li class="nav-item" title={m.cancel()}>
                            <a
                                class="nav-link"
                                href="#!/"
                                onclick={() => triggerParentCancel()}
                                ><i class="bi bi-x-lg"></i> {m.close()}</a>
                        </li>
                    {/if}
                    {#if environment.parentExportSubtitle}
                        <li class="nav-item" title={m.export()}>
                            <a
                                class="nav-link fw-bold"
                                href="#!/"
                                onclick={() => triggerParentExportSubtitle()}
                                ><i class="bi bi-upload"></i> {m.save()}</a>
                        </li>
                    {/if}
                {/if}
            </ul>
            </MenuBar>
        </div>
    </div>
</nav>

<style>
.cc-toolbar-primary {
    position: relative;
    z-index: 20;
    padding-block: 0;
}

.cc-toolbar-primary :global(.container-fluid),
.cc-toolbar-primary :global(.navbar-collapse),
.cc-toolbar-primary :global(.menu-bar-root),
.cc-toolbar-primary :global(.navbar-nav),
.cc-toolbar-primary :global(.nav-item) {
    height: 100%;
}

.cc-toolbar-primary :global(.nav-link) {
    display: flex;
    align-items: center;
    height: 100%;
    padding-block: 0;
}
</style>